import type { Middleware } from '@reduxjs/toolkit';
import { showStackedOverlayLegend, toggleStackedOverlayLegend, updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import type { ConditionStyleId, DynamicStyleId } from '@alltrails/maps/types/Styles';
import { isConditionStyleId } from '@alltrails/maps/utils/overlayStack';
import ConditionsRootState from './ConditionsRootState';
import { focusCondition, middlewareOnly_updateActiveCondition, updateIsPlayingOverlay, updatePreloadedOverlayUTCs } from './conditionsSlice';

const getAppliedConditions = (dynamicStyleIds: DynamicStyleId[] = []) => dynamicStyleIds.filter(isConditionStyleId);

const getRequestedFocus = (action: unknown): ConditionStyleId | undefined => {
  if (focusCondition.match(action)) {
    return action.payload;
  }
  if (toggleStackedOverlayLegend.match(action) && isConditionStyleId(action.payload)) {
    return action.payload;
  }
  return undefined;
};

const getFocusedCondition = (
  previousConditions: ConditionStyleId[],
  appliedConditions: ConditionStyleId[],
  activeCondition: ConditionStyleId | undefined,
  requestedFocus: ConditionStyleId | undefined
) => {
  if (requestedFocus && appliedConditions.includes(requestedFocus)) {
    return requestedFocus;
  }
  const addedConditions = appliedConditions.filter(id => !previousConditions.includes(id));
  if (addedConditions.length) {
    return addedConditions.at(-1);
  }
  return activeCondition && appliedConditions.includes(activeCondition) ? activeCondition : appliedConditions.at(-1);
};

const conditionsMiddleware: Middleware<{}, ConditionsRootState> =
  ({ dispatch, getState }) =>
  next =>
  action => {
    const previousConditions = getAppliedConditions(getState().map.dynamicStyleIds);
    const result = next(action);
    const updatedState = getState();
    const dynamicStyleIds = updatedState.map.dynamicStyleIds || [];
    const appliedConditions = getAppliedConditions(dynamicStyleIds);

    const activeCondition = updatedState.conditions.activeCondition;
    const updatedActiveCondition = getFocusedCondition(previousConditions, appliedConditions, activeCondition, getRequestedFocus(action));

    // When the focused condition changes, update the active condition and reset state
    if (activeCondition !== updatedActiveCondition) {
      const { selectedUTC } = updatedState.conditions;
      dispatch(middlewareOnly_updateActiveCondition(updatedActiveCondition));
      dispatch(updatePreloadedOverlayUTCs(selectedUTC ? [selectedUTC] : []));
      dispatch(updateIsPlayingOverlay(false));

      const { activeStackableOverlayId, isStackedOverlayLegendOpen } = getState().map;
      if (
        updatedActiveCondition &&
        isStackedOverlayLegendOpen &&
        activeStackableOverlayId !== updatedActiveCondition &&
        activeStackableOverlayId &&
        isConditionStyleId(activeStackableOverlayId)
      ) {
        dispatch(showStackedOverlayLegend(updatedActiveCondition));
      }
    }

    // When a user selects groundConditions, automatically select surfaceType
    if (appliedConditions.includes('groundConditions') && !dynamicStyleIds.includes('surfaceType')) {
      dispatch(updateDynamicStyleIds([...dynamicStyleIds, 'surfaceType']));
    }

    // When a user unselects groundConditions, automatically unselect surfaceType
    if (
      (activeCondition === 'groundConditions' || previousConditions.includes('groundConditions')) &&
      !appliedConditions.includes('groundConditions')
    ) {
      if (dynamicStyleIds.includes('surfaceType')) {
        dispatch(updateDynamicStyleIds(dynamicStyleIds.filter(id => id !== 'surfaceType')));
      }
    }

    return result;
  };

export default conditionsMiddleware;
